"use client";

import { useState, useEffect } from "react";
import InterviewChat from "@/components/InterviewChat";
import type { Competency, ChatMessage, FinalReport } from "@/lib/types";
import { useScreenRecorder } from "@/hooks/useScreenRecorder";
import { RecordingIndicator } from "@/components/kolleno/RecordingIndicator";
import { PrepView } from "@/components/kolleno/PrepView";
import { posthog } from "@/lib/posthog";
import { newInterviewId } from "@/lib/analytics";
import { MAX_RESUME_TEXT_LENGTH } from "@/lib/limits";
import { resilientFetch } from "@/lib/interview-save";

const POSITION = "Senior Backend Developer";
const ROLE_SLUG = "senior-backend-developer";

const JD_COMPETENCIES: Competency[] = [
  { skill: "Python & Django", description: "5+ years Python experience, Django framework expert, Django REST Framework" },
  { skill: "Database Design", description: "Excellence in PostgreSQL, complex data modeling, migrations, query optimization" },
  { skill: "API Development", description: "Building robust REST APIs, understanding of web services, integration patterns" },
  { skill: "AWS & Infrastructure", description: "AWS Lambda experience, scalable cloud architecture, deployment practices" },
  { skill: "Testing", description: "Strong testing culture with pytest, TDD practices, test coverage" },
  { skill: "System Architecture", description: "Designing robust backends, integrations between modern and legacy systems" },
  { skill: "Communication & Ownership", description: "Clear written communication, ability to own projects end-to-end, client-facing skills" },
];

const JD_TEXT = `About The Role
We're looking for a developer who takes pride in crafting solutions that help enterprises grow. We are B2B Fintech in Payments, helping enterprises improve financial operations.

Do you…
– enjoy deeply understanding business problems before crafting solutions?
– find satisfaction in designing systems that bring order to complex business operations?
– take pride in building robust architectures that scale reliably?
– excel at modeling complex business processes into elegant data structures?

Here's what makes our work different
We're not just building B2B software – we're creating payment systems that transform how businesses operate. Our enterprise clients trust us to understand their complex processes and turn them into efficient solutions.

Your Day-to-Day
– Design and build robust backends that power business transformation
– Create sophisticated data models that reflect complex business rules
– Develop reliable integrations between modern and legacy systems
– Build powerful APIs that connect disparate business systems
– Implement secure, scalable solutions for business-critical operations

Experience Required
– 5+ years programming experience with Python and/or 2+ years with Django
– Django framework expert
– Django REST Framework and AWS experience (nice to have)
– Experience building and maintaining large-scale applications
– Excellence in database design and optimization
– Understanding of complex data modeling and migrations
– Clear written communicator that prefers emails to meetings

Technologies We Use
Core: Django REST Framework, PostgreSQL, AWS Lambda
Libraries: Pandas, OAuthlib
Testing: pytest`;

type Phase = "landing" | "cv" | "rejected" | "prep" | "interview" | "processing" | "done";

export default function KollenoBackendPage() {
  const { status, error: recError, isDesktop, startRecording, stopAndUpload } = useScreenRecorder();
  const [phase, setPhase] = useState<Phase>("landing");
  const [cvText, setCvText] = useState("");
  const [contacts, setContacts] = useState("");
  const [cvLoading, setCvLoading] = useState(false);
  const [cvError, setCvError] = useState("");
  const [candidateInfo, setCandidateInfo] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [interviewId, setInterviewId] = useState<string | null>(null);

  useEffect(() => {
    posthog.register({ product: "kolleno", role: ROLE_SLUG });
  }, []);

  async function parseCvAndProceed(body: object) {
    setCvLoading(true); setCvError("");
    try {
      const res = await fetch("/api/kolleno/parse-cv", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setCandidateInfo({ ...data, contacts });

      if (data.countryCode === "IN") {
        setPhase("rejected");
        return;
      }

      const logRes = await fetch("/api/admin/log-session", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: contacts || null,
          jobDescription: `Kolleno / ${POSITION} — ${data.name || "Unknown"} from ${data.country || "Unknown"}`,
          paid: true,
          locale: "en",
        }),
      }).catch(() => null);
      const logData = logRes ? await logRes.json().catch(() => ({})) : {};
      setSessionId(logData.sessionId || null);

      setPhase("prep");
    } catch (e: any) {
      setCvError(e.message || "Failed to parse CV");
    } finally {
      setCvLoading(false);
    }
  }

  async function handleCvSubmit() {
    if (!cvText.trim()) return;
    await parseCvAndProceed({ cvText });
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type === "application/pdf") {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = ""; for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      await parseCvAndProceed({ cvBase64: btoa(bin), mimeType: "application/pdf" });
    } else {
      const text = await file.text();
      setCvText(text);
    }
  }

  async function handleInterviewComplete(finalHistory: ChatMessage[]) {
    posthog.capture("interview_completed", {
      interview_id: interviewId,
      theme: "kolleno",
      role: ROLE_SLUG,
      position: POSITION,
      messages_count: finalHistory.length,
      country: candidateInfo?.country || null,
    });
    setPhase("processing");
    try {
      const reportRes = await fetch("/api/report", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ history: finalHistory, locale: "en", expectedCompetencies: JD_COMPETENCIES }),
      });
      if (!reportRes.ok) {
        posthog.capture("kolleno_report_failed", { interview_id: interviewId, role: ROLE_SLUG, status: reportRes.status });
        setPhase("done");
        return;
      }
      const { report }: { report: FinalReport } = await reportRes.json();
      if (!report?.competencies?.length) {
        posthog.capture("kolleno_report_empty", { interview_id: interviewId, role: ROLE_SLUG });
        setPhase("done");
        return;
      }
      posthog.capture("report_generated", {
        interview_id: interviewId,
        theme: "kolleno",
        role: ROLE_SLUG,
        position: POSITION,
        overall_verdict: report?.overall_verdict,
        competencies_count: report?.competencies?.length,
      });

      // resilientFetch retries transient KV blips so we still get the id for the
      // result URL + email; on total failure stop without faking success.
      const saveRes = await resilientFetch({
        url: "/api/kolleno/save-result",
        payload: {
          candidateName: candidateInfo?.name || "Unknown",
          country: candidateInfo?.country || "Unknown",
          contacts: candidateInfo?.contacts || null,
          position: POSITION,
          report,
        },
      });
      if (!saveRes || !saveRes.ok) {
        posthog.capture("kolleno_save_failed", { interview_id: interviewId, role: ROLE_SLUG });
        setPhase("done");
        return;
      }
      const { id } = await saveRes.json();
      const resultUrl = `${window.location.origin}/kolleno/${id}`;

      const avgScore = report.competencies.reduce((s, c) => s + c.score, 0) / report.competencies.length;
      const strengths = report.competencies.filter(c => c.score >= 7).map(c => `${c.skill} (${c.score}/10): ${c.feedback.slice(0, 80)}`);
      const weaknesses = report.competencies.filter(c => c.score < 7).map(c => `${c.skill} (${c.score}/10): ${c.feedback.slice(0, 80)}`);

      const recordingUrl = isDesktop ? await stopAndUpload() : null;

      await fetch("/api/kolleno/send-email", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidateName: candidateInfo?.name || "Unknown",
          country: candidateInfo?.country || "Unknown",
          contacts: candidateInfo?.contacts || null,
          score: Math.round(avgScore * 10) / 10,
          strengths, weaknesses, resultUrl, position: POSITION,
          ...(recordingUrl ? { recordingUrl } : {}),
        }),
      });
    } catch (e) { console.error("Post-interview error:", e); }
    setPhase("done");
  }

  if (phase === "landing") return <LandingView onNext={() => setPhase("cv")} />;
  if (phase === "cv") return (
    <CVView
      cvText={cvText} onCvChange={setCvText}
      contacts={contacts} onContactsChange={setContacts}
      onFileUpload={handleFileUpload}
      onSubmit={handleCvSubmit}
      loading={cvLoading} error={cvError}
    />
  );
  if (phase === "rejected") return <RejectedView />;
  if (phase === "prep") return (
    <PrepView
      candidateName={candidateInfo?.name || "there"}
      position={POSITION}
      isDesktop={isDesktop}
      onStart={async () => {
        if (isDesktop) await startRecording();
        const freshId = newInterviewId();
        setInterviewId(freshId);
        posthog.capture("interview_started", {
          interview_id: freshId,
          theme: "kolleno",
          role: ROLE_SLUG,
          position: POSITION,
          competencies_count: JD_COMPETENCIES.length,
          country: candidateInfo?.country || null,
        });
        setPhase("interview");
      }}
    />
  );
  if (phase === "processing") return <ProcessingView />;
  if (phase === "done") return <DoneView />;

  return (
    <div className="min-h-screen bg-hf-paper">
      <div className="border-b border-hf-line px-6 py-3 flex items-center gap-3">
        <div className="w-7 h-7 rounded bg-gradient-to-br from-hf-primary to-hf-primary-strong flex items-center justify-center font-bold text-xs text-white">K</div>
        <span className="text-hf-ink font-medium text-sm">Kolleno — {POSITION} Interview</span>
        {candidateInfo?.name && <span className="text-hf-faint text-sm">· {candidateInfo.name}</span>}
        <RecordingIndicator status={status} />
      </div>
      <InterviewChat
        competencies={JD_COMPETENCIES}
        onComplete={handleInterviewComplete}
        isPaid={true}
        sessionId={sessionId}
        interviewId={interviewId}
      />
    </div>
  );
}

function LandingView({ onNext }: { onNext: () => void }) {
  return (
    <div className="min-h-screen bg-hf-paper text-hf-ink">
      <div className="border-b border-hf-line px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-gradient-to-br from-hf-primary to-hf-primary-strong flex items-center justify-center font-bold text-sm text-white">K</div>
        <span className="font-semibold text-hf-ink">Kolleno — We're Hiring</span>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        <div>
          <div className="inline-block bg-hf-primary-soft text-hf-primary text-sm px-3 py-1 rounded-full mb-4 border border-hf-primary/20">Open Position</div>
          <h1 className="text-4xl font-bold mb-2">Senior Backend Developer</h1>
          <p className="text-hf-muted text-lg">Kolleno · Remote · Full-time · Stock options</p>
        </div>
        <div className="text-hf-muted leading-relaxed whitespace-pre-line text-sm">{JD_TEXT}</div>
        <button onClick={onNext} className="bg-hf-primary hover:bg-hf-primary-strong text-white font-semibold py-3 px-8 rounded-btn transition-colors text-lg shadow-[0_10px_26px_-10px_rgba(47,107,246,0.6)]">
          Apply Now →
        </button>
      </div>
    </div>
  );
}

function CVView({ cvText, onCvChange, contacts, onContactsChange, onFileUpload, onSubmit, loading, error }: {
  cvText: string; onCvChange: (v: string) => void;
  contacts: string; onContactsChange: (v: string) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void; loading: boolean; error: string;
}) {
  return (
    <div className="min-h-screen bg-hf-paper text-hf-ink">
      <div className="border-b border-hf-line px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-gradient-to-br from-hf-primary to-hf-primary-strong flex items-center justify-center font-bold text-sm text-white">K</div>
        <span className="font-semibold text-hf-ink">Kolleno — Submit Your Resume</span>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-8">
        <div>
          <h2 className="text-2xl font-bold mb-2">Your Resume</h2>
          <p className="text-hf-muted">Upload your CV as a PDF, or paste the text below. Our AI will review it and conduct a voice interview with you.</p>
        </div>
        <label className="flex items-center justify-center gap-3 border-2 border-dashed border-hf-border-strong hover:border-hf-primary rounded-btn p-6 cursor-pointer transition-colors group bg-hf-card">
          <span className="text-3xl">📄</span>
          <div>
            <p className="text-hf-ink group-hover:text-hf-primary transition-colors">Click to upload PDF</p>
            <p className="text-hf-faint text-sm">PDF supported · DOCX/TXT will be read as text</p>
          </div>
          <input type="file" accept=".pdf,.docx,.txt" onChange={onFileUpload} className="hidden" />
        </label>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-hf-line" />
          <span className="text-hf-faint text-sm">or paste text</span>
          <div className="flex-1 h-px bg-hf-line" />
        </div>
        <textarea value={cvText} onChange={(e) => onCvChange(e.target.value.slice(0, MAX_RESUME_TEXT_LENGTH))}
          maxLength={MAX_RESUME_TEXT_LENGTH}
          placeholder="Paste your CV / resume here..." rows={10}
          className="w-full bg-white border border-hf-line rounded-btn px-4 py-3 text-hf-ink placeholder-hf-faint focus:outline-none focus:border-hf-primary resize-none text-sm" />
        <p className={`text-xs text-right ${cvText.length >= MAX_RESUME_TEXT_LENGTH ? "text-red-500" : "text-hf-faint"}`}>
          {cvText.length.toLocaleString()} / {MAX_RESUME_TEXT_LENGTH.toLocaleString()}
        </p>
        <div>
          <label className="block text-sm font-medium text-hf-ink mb-2">
            How can we contact you? <span className="text-hf-faint font-normal">(email, LinkedIn, phone — any format)</span>
          </label>
          <textarea value={contacts} onChange={(e) => onContactsChange(e.target.value)}
            placeholder="e.g. john@example.com · linkedin.com/in/john · +1 555 123 4567"
            rows={2}
            className="w-full bg-white border border-hf-line rounded-btn px-4 py-3 text-hf-ink placeholder-hf-faint focus:outline-none focus:border-hf-primary resize-none text-sm" />
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <button onClick={onSubmit} disabled={loading || !cvText.trim()}
          className="w-full bg-hf-primary hover:bg-hf-primary-strong disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-btn transition-colors flex items-center justify-center gap-2 shadow-[0_10px_26px_-10px_rgba(47,107,246,0.6)]">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Analyzing your CV...</>
            : "Continue to Interview →"}
        </button>
      </div>
    </div>
  );
}

function RejectedView() {
  return (
    <div className="min-h-screen bg-hf-paper text-hf-ink flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-hf-surface border border-hf-line flex items-center justify-center text-3xl mx-auto">🌍</div>
        <h2 className="text-2xl font-bold">Thank you for your interest</h2>
        <p className="text-hf-muted leading-relaxed">We appreciate you taking the time to apply. Unfortunately, your profile doesn't match our current requirements for this position.</p>
        <p className="text-hf-faint text-sm">We'll keep your details on file and will reach out if a suitable opportunity arises in the future.</p>
      </div>
    </div>
  );
}

function ProcessingView() {
  return (
    <div className="min-h-screen bg-hf-paper text-hf-ink flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-hf-primary-soft border border-hf-primary/20 flex items-center justify-center mx-auto">
          <div className="w-10 h-10 rounded-full animate-spin" style={{ borderWidth: 3, borderColor: 'rgba(47,107,246,0.25)', borderTopColor: 'rgb(47,107,246)' }} />
        </div>
        <h2 className="text-2xl font-bold">Processing your interview...</h2>
        <p className="text-hf-muted">Please keep this tab open. We're generating your report and uploading the recording.</p>
        <p className="text-hf-faint text-sm">This usually takes 15–30 seconds.</p>
      </div>
    </div>
  );
}

function DoneView() {
  return (
    <div className="min-h-screen bg-hf-paper text-hf-ink flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-hf-success/10 border border-hf-success/20 flex items-center justify-center text-4xl mx-auto text-hf-success">✓</div>
        <h2 className="text-3xl font-bold">Thank you!</h2>
        <p className="text-hf-muted leading-relaxed text-lg">Your interview has been completed. We'll review your results and get back to you soon.</p>
        <p className="text-hf-faint text-sm">The Kolleno team typically responds within 3–5 business days.</p>
      </div>
    </div>
  );
}
